/**
 * The Monitor's reads and the watch toggle (PRD-0075 B). The inbox is also polled; the live push
 * over SSE and the bell are `useMonitorInbox`'s.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  type AlertDto,
  caseMonitorApi,
  MAX_INBOX_ALERTS,
  type WatchEntityRequest,
  type WatchlistsResponse,
} from '@/services/api/case-monitor'
import { retryUnlessRefused } from '@/services/api/retry'

/** How often the inbox asks for new alerts while it is open. */
const INBOX_POLL_MS = 30_000

export const monitorKeys = {
  all: (caseId: string) => ['case', caseId, 'monitor'] as const,
  watchlists: (caseId: string) => [...monitorKeys.all(caseId), 'watchlists'] as const,
  alerts: (caseId: string) => [...monitorKeys.all(caseId), 'alerts'] as const,
}

export function useWatchlists(caseId: string) {
  return useQuery<WatchlistsResponse>({
    queryKey: monitorKeys.watchlists(caseId),
    queryFn: ({ signal }) => caseMonitorApi.listWatchlists(caseId, signal),
    enabled: Boolean(caseId),
    retry: retryUnlessRefused,
  })
}

/** The case's newest alerts, as many as one read takes; the page filters, counts and groups them. */
export function useMonitorAlerts(caseId: string) {
  return useQuery<AlertDto[]>({
    queryKey: monitorKeys.alerts(caseId),
    queryFn: async ({ signal }) =>
      (await caseMonitorApi.listAlerts(caseId, signal, { limit: MAX_INBOX_ALERTS })).alerts,
    enabled: Boolean(caseId),
    refetchInterval: INBOX_POLL_MS,
    retry: retryUnlessRefused,
  })
}

export type WatchChange =
  | { action: 'watch'; entity: WatchEntityRequest }
  | { action: 'unwatch'; uid: string }

/** Watch or stop watching an entity; the case's watchlists are read again either way. */
export function useWatchChange(caseId: string) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, WatchChange>({
    mutationFn: change =>
      change.action === 'watch'
        ? caseMonitorApi.watch(caseId, change.entity)
        : caseMonitorApi.unwatch(caseId, change.uid),
    onError: () => toast.error(t('cases.monitor.watch.failed')),
    onSettled: () => queryClient.invalidateQueries({ queryKey: monitorKeys.watchlists(caseId) }),
  })
}

/** The uid a pending change is about, if any. */
export function pendingUid(change: WatchChange | undefined, isPending: boolean): string | null {
  if (!isPending || !change) return null
  return change.action === 'watch' ? change.entity.uid : change.uid
}
